import api from '../utils/api';
import {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  UserFilters,
  ActivitySummary,
  AllRolesResponse,
  RolePermission,
  UserSession,
  AuditEntry,
  NotificationPref,
  ApiResponse
} from '../types';

// ─────────────────────────────────────────────────────────────
// USER MANAGEMENT
// ─────────────────────────────────────────────────────────────

// GET /api/users — list all users with optional filters
// Filters become query params: ?role_code=PM&is_active=true&search=rajesh
const getAllUsers = async (filters?: UserFilters): Promise<User[]> => {
  const params: Record<string, any> = {};
  if (filters?.role_code !== undefined) params.role_code = filters.role_code;
  if (filters?.user_type !== undefined) params.user_type = filters.user_type;
  if (filters?.is_active !== undefined) params.is_active = filters.is_active;
  if (filters?.search)                  params.search    = filters.search;

  const response = await api.get<ApiResponse<User[]>>('/users', { params });
  return response.data.data || [];
};

// GET /api/users/:id — get one user
const getUserById = async (id: number): Promise<User> => {
  const response = await api.get<ApiResponse<User>>(`/users/${id}`);
  return response.data.data!;
};

// GET /api/users/by-role/:role_code — users for dropdown menus
// Used by Project module (PM dropdown), Quality (inspector dropdown) etc.
const getUsersByRole = async (role_code: string): Promise<User[]> => {
  const response = await api.get<ApiResponse<User[]>>(`/users/by-role/${role_code}`);
  return response.data.data || [];
};

// GET /api/users/activity-summary — for MD dashboard
const getActivitySummary = async (): Promise<ActivitySummary> => {
  const response = await api.get<ApiResponse<ActivitySummary>>('/users/activity-summary');
  return response.data.data!;
};

// POST /api/users — create single user
const createUser = async (data: CreateUserRequest): Promise<User> => {
  const response = await api.post<ApiResponse<User>>('/users', data);
  return response.data.data!;
};

// POST /api/users/bulk — create up to 50 users at once
const bulkCreateUsers = async (users: CreateUserRequest[]): Promise<{
  created: User[];
  errors:  { username: string; error: string }[];
}> => {
  const response = await api.post('/users/bulk', { users });
  return response.data;
};

// PUT /api/users/:id — update user
const updateUser = async (id: number, data: UpdateUserRequest): Promise<User> => {
  const response = await api.put<ApiResponse<User>>(`/users/${id}`, data);
  return response.data.data!;
};

// DELETE /api/users/:id — deactivate user (soft delete)
const deactivateUser = async (id: number): Promise<void> => {
  await api.delete(`/users/${id}`);
};

// POST /api/users/:id/reset-password — admin resets password
const resetPassword = async (id: number, new_password?: string): Promise<void> => {
  await api.post(`/users/${id}/reset-password`, { new_password });
};

// ─────────────────────────────────────────────────────────────
// ROLES & PERMISSIONS
// ─────────────────────────────────────────────────────────────

// GET /api/users/roles/all — all roles grouped by role_code
const getAllRoles = async (): Promise<AllRolesResponse> => {
  const response = await api.get<ApiResponse<AllRolesResponse>>('/users/roles/all');
  return response.data.data!;
};

// GET /api/users/roles/:role_code — one role's permissions
const getRolePermissions = async (role_code: string): Promise<RolePermission[]> => {
  const response = await api.get<ApiResponse<RolePermission[]>>(`/users/roles/${role_code}`);
  return response.data.data || [];
};

// PUT /api/users/roles/:role_code — update role permissions
const updateRolePermissions = async (
  role_code: string,
  module: string,
  perms: Partial<RolePermission>
): Promise<void> => {
  await api.put(`/users/roles/${role_code}`, { module, ...perms });
};

// ─────────────────────────────────────────────────────────────
// SESSIONS
// ─────────────────────────────────────────────────────────────

// GET /api/users/:id/sessions — list active sessions
const getUserSessions = async (id: number): Promise<UserSession[]> => {
  const response = await api.get<ApiResponse<UserSession[]>>(`/users/${id}/sessions`);
  return response.data.data || [];
};

// DELETE /api/users/:id/sessions — revoke all sessions (force logout)
const revokeAllSessions = async (id: number): Promise<void> => {
  await api.delete(`/users/${id}/sessions`);
};

// ─────────────────────────────────────────────────────────────
// NOTIFICATION PREFERENCES
// ─────────────────────────────────────────────────────────────

// GET /api/users/:id/notification-prefs
const getNotifPrefs = async (id: number): Promise<NotificationPref[]> => {
  const response = await api.get<ApiResponse<NotificationPref[]>>(
    `/users/${id}/notification-prefs`
  );
  return response.data.data || [];
};

// PUT /api/users/:id/notification-prefs
const updateNotifPrefs = async (
  id: number,
  prefs: Partial<NotificationPref>[]
): Promise<void> => {
  await api.put(`/users/${id}/notification-prefs`, { prefs });
};

// ─────────────────────────────────────────────────────────────
// AUDIT LOG
// ─────────────────────────────────────────────────────────────

// GET /api/users/audit/log — filterable audit trail
const getAuditLog = async (filters?: {
  table_name?:  string;
  action?:      string;
  from_date?:   string;
  to_date?:     string;
  limit?:       number;
}): Promise<AuditEntry[]> => {
  const response = await api.get<ApiResponse<AuditEntry[]>>(
    '/users/audit/log',
    { params: filters }
  );
  return response.data.data || [];
};

const userService = {
  getAllUsers,
  getUserById,
  getUsersByRole,
  getActivitySummary,
  createUser,
  bulkCreateUsers,
  updateUser,
  deactivateUser,
  resetPassword,
  getAllRoles,
  getRolePermissions,
  updateRolePermissions,
  getUserSessions,
  revokeAllSessions,
  getNotifPrefs,
  updateNotifPrefs,
  getAuditLog,
};

export default userService;